import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import axios from 'axios';
import API_BASE from '../apiBase';
import { getStoredUser, setStoredUser } from '../utils/userStorage';
import { PROFILE_LIMITS, sanitizeProfileForm, validateProfileForm, isValidProfileName, isValidProfilePhone } from '../utils/profileValidation';

function ProfileSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
    phone: '',
    role: 'user',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    },
    marketing_email_opt_out: false
  });

  useEffect(() => {
    const loadProfile = async () => {
      if (!getStoredUser()?.id) {
        navigate('/login');
        return;
      }

      try {
        const { data } = await axios.get(`${API_BASE}/api/users/me`);
        if (!data?.id) {
          navigate('/login');
          return;
        }
        setStoredUser(data);
        setUser(data);
        setFormData({
          name: data.name || '',
          email: data.email || '',
          company_name: data.company_name || '',
          phone: data.phone || '',
          role: data.role || 'user',
          address: {
            street1: data.address?.street1 || '',
            street2: data.address?.street2 || '',
            city: data.address?.city || '',
            state: data.address?.state || '',
            postal_code: data.address?.postal_code || '',
            country: data.address?.country || ''
          },
          marketing_email_opt_out: data.marketing_email_opt_out || false
        });
      } catch {
        navigate('/login');
      }
    };

    loadProfile();
  }, [navigate]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const fieldLimits = {
    name: PROFILE_LIMITS.name,
    company_name: PROFILE_LIMITS.company_name,
    phone: PROFILE_LIMITS.phone,
    street: PROFILE_LIMITS.street,
    city: PROFILE_LIMITS.city,
    state: PROFILE_LIMITS.state,
    postal_code: PROFILE_LIMITS.postal_code,
    country: PROFILE_LIMITS.country,
  };

  const profileValidationErrors = validateProfileForm(sanitizeProfileForm(formData));
  const hasInvalidStoredProfile = user && profileValidationErrors.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const sanitized = sanitizeProfileForm(formData);
    const validationErrors = validateProfileForm(sanitized);
    if (validationErrors.length > 0) {
      setError(validationErrors[0]);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE}/api/users/${user.id}`,
        {
          name: sanitized.name,
          company_name: sanitized.company_name,
          phone: sanitized.phone,
          role: sanitized.role,
          address: sanitized.address,
          marketing_email_opt_out: formData.marketing_email_opt_out
        }
      );

      // Update stored user data
      const updatedUser = { ...user, ...response.data };
      setStoredUser(updatedUser);
      setUser(updatedUser);
      window.dispatchEvent(new Event('userUpdated'));

      setSuccess('Profile updated successfully!');
    } catch (err) {
      const details = err?.response?.data?.details;
      setError(details || err?.response?.data?.error || `Failed to update profile: ${err.message}`);
    }
    setLoading(false);
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 4, color: '#1f1f1f', fontWeight: 700 }}>
        Profile Settings
      </Typography>

      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {hasInvalidStoredProfile && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Your saved profile contains invalid characters or values (for example from security testing).
              Update the highlighted fields below and save to fix your display name in the header.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h6" sx={{ mb: 3, color: '#1f1f1f', fontWeight: 600 }}>
              Contact Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  inputProps={{ maxLength: fieldLimits.name }}
                  error={Boolean(formData.name) && !isValidProfileName(formData.name)}
                  helperText={
                    formData.name && !isValidProfileName(formData.name)
                      ? 'Use 4–50 letters only (no symbols like < or >)'
                      : ''
                  }
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  disabled
                  sx={{ mb: 2 }}
                  helperText="Email cannot be changed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  inputProps={{ maxLength: fieldLimits.company_name }}
                  sx={{ mb: 2 }}
                  placeholder="Enter your company name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                  inputProps={{ maxLength: fieldLimits.phone }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    label="Role"
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="developer">Developer</MenuItem>
                    <MenuItem value="manager">Manager</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mb: 3, mt: 4, color: '#1f1f1f', fontWeight: 600 }}>
              Address Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address 1"
                  value={formData.address.street1}
                  onChange={(e) => handleInputChange('address.street1', e.target.value)}
                  inputProps={{ maxLength: fieldLimits.street }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address 2 (Optional)"
                  value={formData.address.street2}
                  onChange={(e) => handleInputChange('address.street2', e.target.value)}
                  inputProps={{ maxLength: fieldLimits.street }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  inputProps={{ maxLength: fieldLimits.city }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State/Province"
                  value={formData.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  inputProps={{ maxLength: fieldLimits.state }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.address.postal_code}
                  onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
                  inputProps={{ maxLength: fieldLimits.postal_code }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.address.country}
                  onChange={(e) => handleInputChange('address.country', e.target.value)}
                  inputProps={{ maxLength: fieldLimits.country }}
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" sx={{ mb: 3, color: '#1f1f1f', fontWeight: 600 }}>
              Communication Preferences
            </Typography>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.marketing_email_opt_out}
                    onChange={(e) => handleInputChange('marketing_email_opt_out', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#000',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#000',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formData.marketing_email_opt_out ? 'Opted Out' : 'Opted In'} - Marketing Emails
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                      {formData.marketing_email_opt_out
                        ? 'You will not receive marketing emails from us. You can opt back in at any time.'
                        : 'Receive product updates, tips, and promotional content via email. You can opt out at any time.'}
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/app/dashboard')}
                sx={{ px: 4, py: 1.5 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  bgcolor: '#1f1f1f', 
                  color: '#fff',
                  '&:hover': { bgcolor: '#1f1f1f' }
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

export default ProfileSettings; 